// Start screen shown over the (paused) game on first load. Pick a difficulty, then START.
// Inputs: click, keyboard (arrows pick, Enter/Space start), and gamepad (d-pad/stick pick,
// A starts). Always defaults to Normal on load. Gamepad is polled via requestAnimationFrame
// because the sim loop (which polls Input) isn't running yet.

import type { DifficultyKey } from '../game/balance';
import { adaptiveHint, A_BTN } from './hint';
import { looksXbox, setInputMode } from '../core/inputMode';
import { isCompactTouchLayout } from './platform';

const FONT = '15px "Silkscreen", ui-monospace, monospace';

export class StartUI {
  private readonly overlay: HTMLDivElement;
  private readonly modeButtons = new Map<DifficultyKey, HTMLButtonElement>();
  private readonly modeHint: HTMLDivElement;
  private readonly onStartCb: (difficulty: DifficultyKey, secret: boolean) => void;
  private readonly secretFlash: HTMLDivElement;
  private readonly compact = isCompactTouchLayout();
  private selected: DifficultyKey = 'normal';
  private started = false;
  private padRaf = 0;
  private readonly prevPad = { left: false, right: false, a: false, y: false };

  constructor(mount: HTMLElement, onStart: (difficulty: DifficultyKey, secret: boolean) => void) {
    this.onStartCb = onStart;
    const compact = this.compact;

    this.overlay = document.createElement('div');
    Object.assign(this.overlay.style, {
      position: 'absolute',
      inset: '0',
      display: 'grid',
      placeItems: 'center',
      background: 'rgba(8,6,14,0.78)',
      zIndex: '40',
      pointerEvents: 'auto',
      userSelect: 'none',
      font: compact ? '12px "Silkscreen", ui-monospace, monospace' : FONT,
      color: '#e8e4f0',
      textAlign: 'center',
    });

    const box = document.createElement('div');
    Object.assign(box.style, {
      padding: compact ? '14px' : '24px',
      maxWidth: compact ? 'min(560px, 94vw)' : 'none',
    });

    const title = document.createElement('div');
    title.textContent = 'BREWMANCER';
    Object.assign(title.style, {
      font: `${compact ? 30 : 40}px "Silkscreen", ui-monospace, monospace`,
      color: '#a78bfa',
      letterSpacing: compact ? '2px' : '3px',
      textShadow: `${compact ? 2 : 3}px ${compact ? 2 : 3}px 0 #2a2440`,
    });

    const sub = document.createElement('div');
    sub.textContent = 'Brew elements · unlock powers · outlast the horde';
    Object.assign(sub.style, { color: '#cdc7dd', fontSize: compact ? '10px' : '12px', margin: compact ? '8px 0 12px' : '14px 0 18px' });

    const modeRow = document.createElement('div');
    Object.assign(modeRow.style, { display: 'flex', gap: compact ? '8px' : '10px', justifyContent: 'center', margin: '0 0 6px' });
    this.modeHint = document.createElement('div');
    Object.assign(this.modeHint.style, {
      color: '#9a93b0',
      fontSize: compact ? '9px' : '11px',
      minHeight: compact ? '12px' : '14px',
      marginBottom: compact ? '12px' : '18px',
    });

    const MODES: ReadonlyArray<readonly [DifficultyKey, string]> = [
      ['normal', 'NORMAL'],
      ['easy', 'EASY (KIDS)'],
    ];
    for (const [key, label] of MODES) {
      const b = document.createElement('button');
      b.textContent = label;
      Object.assign(b.style, {
        font: `${compact ? 11 : 14}px "Silkscreen", ui-monospace, monospace`,
        padding: compact ? '6px 10px' : '8px 16px',
        border: '2px solid #3a3450',
        borderRadius: '6px',
        cursor: 'pointer',
      });
      b.addEventListener('click', () => this.select(key));
      this.modeButtons.set(key, b);
      modeRow.appendChild(b);
    }
    this.paint();

    const startBtn = document.createElement('button');
    startBtn.textContent = '▶ START';
    Object.assign(startBtn.style, {
      font: `${compact ? 17 : 22}px "Silkscreen", ui-monospace, monospace`,
      padding: compact ? '9px 24px' : '12px 34px',
      color: '#2a1a00',
      background: 'linear-gradient(#ffd24a, #f0a81e)',
      border: '3px solid #6b4a08',
      borderRadius: '8px',
      boxShadow: `0 ${compact ? 4 : 5}px 0 #6b4a08`,
      cursor: 'pointer',
      letterSpacing: '1px',
    });
    startBtn.addEventListener('click', () => this.begin());

    const hint = compact
      ? document.createElement('div')
      : adaptiveHint('Arrows pick · Enter start · weapons auto-fire · survive 15:00', `D-pad pick · ${A_BTN} start · weapons auto-fire · survive 15:00`);
    if (compact) hint.textContent = 'SURVIVE 15:00';
    Object.assign(hint.style, { color: '#9a93b0', fontSize: compact ? '9px' : '11px', marginTop: compact ? '14px' : '20px', lineHeight: '1.45' });

    box.append(title, sub, modeRow, this.modeHint, startBtn, hint);
    this.overlay.appendChild(box);

    // Secret kid-mode celebration (flashed briefly when EASY + Y unlocks it), then the run begins.
    this.secretFlash = document.createElement('div');
    Object.assign(this.secretFlash.style, {
      position: 'absolute',
      inset: '0',
      display: 'none',
      placeItems: 'center',
      background: 'rgba(8,6,14,0.92)',
      color: '#ffd24a',
      textAlign: 'center',
      font: `${compact ? 26 : 34}px "Silkscreen", ui-monospace, monospace`,
      textShadow: '3px 3px 0 #6b4a08',
      letterSpacing: '2px',
    });
    this.secretFlash.innerHTML =
      `<div>&#10022; SECRET MODE &#10022;<div style="font-size:${compact ? 12 : 16}px;color:#fff;margin-top:${compact ? 10 : 14}px;letter-spacing:1px">ALL POWERS UNLOCKED!</div></div>`;
    this.overlay.appendChild(this.secretFlash);

    mount.appendChild(this.overlay);

    window.addEventListener('keydown', this.onKey);
    this.startPadPoll();
  }

  private select(key: DifficultyKey): void {
    this.selected = key;
    this.paint();
  }

  private paint(): void {
    this.modeButtons.forEach((b, key) => {
      const on = key === this.selected;
      b.style.borderColor = on ? '#a78bfa' : '#3a3450';
      b.style.background = on ? 'rgba(120,90,220,0.35)' : 'rgba(20,16,30,0.6)';
      b.style.color = on ? '#fff' : '#9a93b0';
    });
    this.modeHint.textContent = this.compact
      ? this.selected === 'easy'
        ? 'Fewer monsters · not ranked'
        : 'Full challenge · ranked'
      : this.selected === 'easy'
        ? 'Gentler enemies · faster XP · reduced damage (not ranked)'
        : 'The full challenge - runs count on the leaderboard';
  }

  private begin(secret = false): void {
    if (this.started) return;
    this.started = true;
    if (secret) {
      this.secretFlash.style.display = 'grid'; // celebrate, then start in secret mode
      window.setTimeout(() => this.finish(true), 1100);
    } else {
      this.finish(false);
    }
  }

  private finish(secret: boolean): void {
    this.overlay.style.display = 'none';
    window.removeEventListener('keydown', this.onKey);
    if (this.padRaf !== 0) {
      cancelAnimationFrame(this.padRaf);
      this.padRaf = 0;
    }
    this.onStartCb(this.selected, secret);
  }

  private readonly onKey = (e: KeyboardEvent): void => {
    setInputMode('keyboard');
    if (e.code === 'Enter' || e.code === 'Space') {
      e.preventDefault();
      this.begin();
    } else if (e.code === 'ArrowLeft') {
      e.preventDefault();
      this.select('normal');
    } else if (e.code === 'ArrowRight') {
      e.preventDefault();
      this.select('easy');
    } else if (e.code === 'KeyY' && this.selected === 'easy') {
      e.preventDefault();
      this.begin(true); // secret kid mode (only from EASY)
    }
  };

  private startPadPoll(): void {
    // Treat held buttons as down so the first action needs a fresh press.
    this.prevPad.left = true;
    this.prevPad.right = true;
    this.prevPad.a = true;
    this.prevPad.y = true;
    const loop = (): void => {
      if (this.started) {
        this.padRaf = 0;
        return;
      }
      this.pollPad();
      this.padRaf = requestAnimationFrame(loop);
    };
    this.padRaf = requestAnimationFrame(loop);
  }

  private pollPad(): void {
    const pads = typeof navigator.getGamepads === 'function' ? navigator.getGamepads() : [];
    let gp: Gamepad | null = null;
    for (let i = 0; i < pads.length; i++) {
      const p = pads[i];
      if (p !== null) {
        gp = p;
        break;
      }
    }
    if (gp === null) return;
    const ax = gp.axes[0] ?? 0;
    const left = (gp.buttons[14]?.pressed ?? false) || ax < -0.5;
    const right = (gp.buttons[15]?.pressed ?? false) || ax > 0.5;
    const a = gp.buttons[0]?.pressed ?? false;
    const y = gp.buttons[3]?.pressed ?? false; // Y (Xbox) / Triangle (PS): secret kid mode
    if (left || right || a || y) setInputMode('gamepad', looksXbox(gp.id));
    if (left && !this.prevPad.left) this.select('normal');
    else if (right && !this.prevPad.right) this.select('easy');
    if (y && !this.prevPad.y && this.selected === 'easy') this.begin(true);
    else if (a && !this.prevPad.a) this.begin();
    this.prevPad.left = left;
    this.prevPad.right = right;
    this.prevPad.a = a;
    this.prevPad.y = y;
  }
}
